import { Form, Space, TableProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { EyeIcon } from 'assets/svg';
import { ButtonAction } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { TEAM_TYPE } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import React, { forwardRef, memo, useEffect, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import formatNumber, {
  formatCurrency,
  formatDate,
  formatNumber_v2,
  generateUUID,
} from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  refetch: (isExport?: boolean, headerRow?: any) => void;
}

const TablePaymentSummary = forwardRef((props: IProps, ref: any) => {
  const { data = {}, loading, refetch } = props;
  const [form] = useForm();
  const navigate = useNavigate();

  useEffect(() => {
    // console.log('data', data);
  }, []);

  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      width: 50,
      align: 'center',
      render: (_, __, index) =>
        data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
        index +
        1,
    },
    {
      title: 'Mã THTT',
      width: 100,
      dataIndex: 'id',
    },
    {
      title: 'Khách hàng',
      width: 150,
      dataIndex: ['tcustomer', 'sapCustomerId'],
    },
    {
      title: 'Tên Khách hàng',
      width: 180,
      dataIndex: ['tcustomer', 'storeName'],
    },
    {
      title: 'Mã KH cha',
      width: 150,
      dataIndex: ['tcustomer', 'mainCustomerId'],
    },
    {
      title: 'Loại đội',
      width: 100,
      dataIndex: ['tteamSale', 'teamType'],
      render: (value) =>
        !value ? 'Kế toán' : value === TEAM_TYPE.stay ? 'Tại chỗ' : 'Chào hàng',
    },
    {
      title: 'Mã đội',
      width: 100,
      dataIndex: ['tteamSale', 'subId'],
    },
    {
      title: 'Tên đội',
      width: 150,
      dataIndex: ['tteamSale', 'name'],
    },
    {
      title: 'Giá bóng',
      width: 100,
      dataIndex: 'marketPrice',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Tổng thanh toán',
      width: 150,
      dataIndex: 'finalPrice',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber(value),
    },
    {
      title: 'KH ghi nợ',
      width: 100,
      dataIndex: 'customerDebitId',
      align: 'right',
      type: 'number',
      // render: (value) => formatNumber(value),
    },
    {
      title: 'KH nợ:Q10',
      width: 100,
      dataIndex: 'paymentCustomerDebitQ10',
      align: 'right',
      type: 'number',
      render: (value) =>
        formatCurrency({
          value: Number(Number(value).toFixed(4)),
          format: 4,
        }) || '_',
    },
    {
      title: 'KH nợ: Tiền mặt',
      width: 170,
      dataIndex: 'paymentCustomerDebitAmount',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber(value),
    },
    {
      title: 'PNJ nợ: Q10',
      width: 130,
      dataIndex: 'paymentPnjpDebitQ10',
      align: 'right',
      type: 'number',
      render: (value) =>
        formatCurrency({
          value: Number(Number(value).toFixed(4)),
          format: 4,
        }) || '_',
    },
    {
      title: 'PNJ nợ: Tiền mặt',
      width: 170,
      dataIndex: 'pnjpDebitAmount',
      align: 'right',
      type: 'number',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Chi nhánh',
      width: 100,
      dataIndex: ['tplant', 'id'],
    },
    {
      title: 'Trạng thái',
      width: 100,
      dataIndex: ['tstatus', 'name'],
    },
    {
      title: 'Người tạo',
      width: 170,
      dataIndex: ['tcreateUser', 'fullName'],
    },
    {
      title: 'Ngày tạo',
      width: 170,
      type: 'date',
      dataIndex: 'createDate',
      render: (value) => (value ? formatDate(value) : '-'),
    },
    {
      title: 'Người cập nhật',
      width: 170,
      dataIndex: ['tmodifyUser', 'fullName'],
    },
    {
      title: 'Ngày cập nhật',
      width: 170,
      type: 'date',
      dataIndex: 'modifyDate',
      render: (value) => (value ? formatDate(value) : '-'),
    },
    {
      fixed: 'right',
      align: 'center',
      width: 70,
      render: (__, record, index) => (
        <Space size={'middle'} className="text-lg ">
          <ButtonAction
            tooltip="Xem chi tiết"
            onClick={() =>
              navigate(path.PAYMENT_SUMMARY_DETAIL, {
                state: {
                  sumPaymentId: record?.id,
                  customerId: record?.tcustomer?.id,
                  ...(record?.tteamSale
                    ? {
                        teamId: record?.tteamSale?.id,
                        teamTypeId: record?.tteamSale?.teamType,
                      }
                    : {
                        teamId: undefined,
                        teamTypeId: TEAM_TYPE.stay,
                      }),
                  isAdd: false,
                },
              })
            }
          >
            <EyeIcon />
          </ButtonAction>
        </Space>
      ),
    },
  ];

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    // Tạo columns cho export với dữ liệu đã được xử lý
    const exportColumns = [
      { title: 'STT', dataIndex: 'index', width: 50 },
      { title: 'Mã THTT', dataIndex: 'id', width: 100 },
      {
        title: 'Khách hàng',
        dataIndex: ['tcustomer', 'sapCustomerId'],
        width: 150,
      },
      {
        title: 'Tên Khách hàng',
        dataIndex: ['tcustomer', 'storeName'],
        width: 180,
      },
      {
        title: 'Mã KH cha',
        dataIndex: ['tcustomer', 'mainCustomerId'],
        width: 150,
      },
      { title: 'Loại đội', dataIndex: 'teamTypeFormatted', width: 100 },
      { title: 'Mã đội', dataIndex: ['tteamSale', 'subId'], width: 100 },
      { title: 'Tên đội', dataIndex: ['tteamSale', 'name'], width: 150 },
      { title: 'Giá bóng', dataIndex: 'marketPriceFormatted', width: 100 },
      {
        title: 'Tổng thanh toán',
        dataIndex: 'finalPriceFormatted',
        width: 150,
      },
      { title: 'KH ghi nợ', dataIndex: 'customerDebitId', width: 100 },
      {
        title: 'KH nợ:Q10',
        dataIndex: 'paymentCustomerDebitQ10Formatted',
        width: 100,
      },
      {
        title: 'KH nợ: Tiền mặt',
        dataIndex: 'paymentPnjpDebitAmountFormatted',
        width: 170,
      },
      {
        title: 'PNJ nợ: Q10',
        dataIndex: 'paymentPnjpDebitQ10Formatted',
        width: 130,
      },
      {
        title: 'PNJ nợ: Tiền mặt',
        dataIndex: 'pnjpDebitAmountFormatted',
        width: 170,
      },
      { title: 'Chi nhánh', dataIndex: ['tplant', 'id'], width: 100 },
      { title: 'Trạng thái', dataIndex: ['tstatus', 'name'], width: 100 },
      {
        title: 'Người tạo',
        dataIndex: ['tcreateUser', 'fullName'],
        width: 170,
      },
      { title: 'Ngày tạo', dataIndex: 'createDateFormatted', width: 170 },
      {
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
        width: 170,
      },
      { title: 'Ngày cập nhật', dataIndex: 'modifyDateFormatted', width: 170 },
    ];

    // Xử lý dữ liệu trước khi export
    const processedData = data.items?.map((item: any, index: number) => ({
      ...item,
      index: index + 1,
      // Map loại đội thành tiếng Việt
      teamTypeFormatted: !item.tteamSale?.teamType
        ? 'Kế toán'
        : item.tteamSale.teamType === TEAM_TYPE.stay
          ? 'Tại chỗ'
          : 'Chào hàng',
      // Format các trường số
      marketPriceFormatted: formatNumber(item.marketPrice),
      finalPriceFormatted: formatNumber(item.finalPrice),
      paymentPnjpDebitAmountFormatted: formatNumber(
        item.paymentPnjpDebitAmount,
      ),
      pnjpDebitAmountFormatted: formatNumber(item.pnjpDebitAmount),
      // Format cột KH nợ:Q10 và PNJ nợ:Q10
      paymentCustomerDebitQ10Formatted:
        formatCurrency({
          value: Number(Number(item.paymentCustomerDebitQ10).toFixed(4)),
          format: 4,
        }) || '_',
      paymentPnjpDebitQ10Formatted:
        formatCurrency({
          value: Number(Number(item.paymentPnjpDebitQ10).toFixed(4)),
          format: 4,
        }) || '_',
      // Format ngày tháng
      createDateFormatted: item.createDate ? formatDate(item.createDate) : '-',
      modifyDateFormatted: item.modifyDate ? formatDate(item.modifyDate) : '-',
    }));

    refetch(true, exportColumns);
  };

  const handleClickTHTT = (record: any) => {
    console.log('record', record);
  };
  return (
    <Content>
      <Form form={form}>
        <TableComponent
          scroll={{ x: 'max-content' }}
          columns={columns}
          // dataSource={[{}]}
          dataSource={data.items}
          rowKey={(record: any) => record?.id || generateUUID()}
          loading={loading}
          pagination={{
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
});

export default memo(TablePaymentSummary);
